/**
 * optionTemplates — the option-strategy ready-mades, as ONE pure module.
 *
 * BYTE-IDENTICAL COPIES (scripts/check-engine-parity.mjs):
 *   frontend/src/utils/optionTemplates.ts          (source of truth)
 *   backend/src/services/options/optionTemplates.ts
 *
 * Why one module: an underlying price level (NB / NS / BB / BS) can carry an
 * option strategy that is built at the moment the level FIRES, hours or days
 * after the user chose it. The dialog that previews "if it fired now" and the
 * server that actually builds the legs must pick the same strikes from the
 * same chain, so they run these exact bytes. The Options Workspace's
 * ready-made cards use them too.
 *
 * Zero imports on purpose (the two copies live at different paths). Pure: no
 * clock, no network — callers pass `nowMs`.
 *
 * Units are Delta-native: prices are USD per 1 unit of the base asset, iv is a
 * fraction (0.43 = 43%), settleMs is epoch ms of settlement.
 */

export type TplSide = 'BUY' | 'SELL';
export type TplOptType = 'CE' | 'PE';
export type TplOutlook = 'Bullish' | 'Bearish' | 'Neutral' | 'Volatile';

/** The fields of a live chain quote the templates read (the workspace's WsQuote is a superset). */
export interface TplQuote {
  symbol: string;
  mark: number | null; bid: number | null; ask: number | null;
  iv: number | null; delta: number | null;
}
export interface TplRow { strike: number; ce: TplQuote | null; pe: TplQuote | null }
export interface TplExpiry { iso: string; settleMs: number }

export interface TplCtx { rows: TplRow[]; spot: number; atm: number; T: number }
export interface TplLegSpec { action: TplSide; t: TplOptType; strike: number; lots?: number }

export interface OptionTemplate {
  id: string; name: string; outlook: TplOutlook; blurb: string;
  /** Mini payoff glyph (SVG polyline points in a 40x20 box, y down). */
  glyph: string;
  build: (ctx: TplCtx) => TplLegSpec[] | null;
}

// ── math ──────────────────────────────────────────────────────────────────────

const YEAR_MS = 365 * 24 * 3600 * 1000;

/** Standard normal CDF (Abramowitz-Stegun 7.1.26, |err| < 7.5e-8). */
function normCdf(x: number): number {
  const a1 = 0.254829592, a2 = -0.284496736, a3 = 1.421413741, a4 = -1.453152027, a5 = 1.061405429, p = 0.3275911;
  const sign = x < 0 ? -1 : 1;
  const z = Math.abs(x) / Math.SQRT2;
  const t = 1 / (1 + p * z);
  const y = 1 - (((((a5 * t + a4) * t) + a3) * t + a2) * t + a1) * t * Math.exp(-z * z);
  return 0.5 * (1 + sign * y);
}

/** Black-Scholes (r = 0) premium; intrinsic at/after expiry or with no vol. */
export function tplBsPrice(t: TplOptType, S: number, K: number, tYears: number, sigma: number): number {
  const intrinsic = t === 'CE' ? Math.max(0, S - K) : Math.max(0, K - S);
  if (!(tYears > 0) || !(sigma > 0) || !(S > 0) || !(K > 0)) return intrinsic;
  const sd = sigma * Math.sqrt(tYears);
  const d1 = (Math.log(S / K) + 0.5 * sd * sd) / sd;
  const d2 = d1 - sd;
  return t === 'CE' ? S * normCdf(d1) - K * normCdf(d2) : K * normCdf(-d2) - S * normCdf(-d1);
}

/** Black-Scholes (r = 0) delta per 1 unit of base. */
export function tplBsDelta(t: TplOptType, S: number, K: number, tYears: number, sigma: number): number {
  if (!(tYears > 0) || !(sigma > 0) || !(S > 0) || !(K > 0)) {
    const itm = t === 'CE' ? S > K : S < K;
    return itm ? (t === 'CE' ? 1 : -1) : 0;
  }
  const sd = sigma * Math.sqrt(tYears);
  const d1 = (Math.log(S / K) + 0.5 * sd * sd) / sd;
  return t === 'CE' ? normCdf(d1) : normCdf(d1) - 1;
}

// ── chain helpers ─────────────────────────────────────────────────────────────

export const tplQuoteOf = (row: TplRow | undefined | null, t: TplOptType): TplQuote | null =>
  (row ? (t === 'CE' ? row.ce : row.pe) : null);

/** The live mark (falls back to mid, then whichever side is quoted). */
export function tplMarkOf(q: TplQuote | null | undefined): number | null {
  if (!q) return null;
  if (q.mark != null && q.mark > 0) return q.mark;
  if (q.bid && q.ask) return (q.bid + q.ask) / 2;
  return q.ask || q.bid || null;
}

/** ATM strike = the listed strike nearest to spot. */
export function tplAtm(rows: TplRow[], spot: number): number | null {
  if (!rows.length || !(spot > 0)) return null;
  let best = rows[0];
  for (let i = 1; i < rows.length; i++) if (Math.abs(rows[i].strike - spot) < Math.abs(best.strike - spot)) best = rows[i];
  return best.strike;
}

/** Strike whose |delta| is closest to `target` on the given side (OTM side only). */
function byDelta(ctx: TplCtx, t: TplOptType, target: number): number | null {
  let best: number | null = null, bestD = Infinity;
  for (const r of ctx.rows) {
    if (t === 'CE' ? r.strike < ctx.atm : r.strike > ctx.atm) continue;
    const q = tplQuoteOf(r, t);
    let d = q ? q.delta : null;
    if (d == null && q && q.iv) d = tplBsDelta(t, ctx.spot, r.strike, ctx.T, q.iv); // no greek on the ticker → BS from its IV
    if (d == null || tplMarkOf(q) == null) continue;
    const diff = Math.abs(Math.abs(d) - target);
    if (diff < bestD) { bestD = diff; best = r.strike; }
  }
  return best;
}

/** The listed (priced) strike `steps` beyond `k` on the OTM side — the wing. */
function wing(ctx: TplCtx, t: TplOptType, k: number, steps: number): number | null {
  const ks = ctx.rows.filter(r => tplMarkOf(tplQuoteOf(r, t)) != null).map(r => r.strike).sort((a, b) => a - b);
  const i = ks.indexOf(k);
  if (i < 0) return null;
  const j = t === 'CE' ? i + steps : i - steps;
  const v = ks[Math.max(0, Math.min(ks.length - 1, j))];
  return v == null ? null : v;
}

/** Side quotes (strike → mark) for one option type. */
export const tplSideQuotes = (rows: TplRow[], t: TplOptType) =>
  rows.map(r => ({ strike: r.strike, price: tplMarkOf(tplQuoteOf(r, t)) || 0 })).filter(q => q.price > 0);

/** Strike on side `t` whose price is closest to `target`, never crossing `limit`. */
export function tplMatchStrike(t: TplOptType, quotes: Array<{ strike: number; price: number }>, target: number, limit: number | null): { strike: number; price: number; clamped: boolean } | null {
  const ok = quotes.filter(q => q.price > 0 && (limit == null || (t === 'CE' ? q.strike >= limit : q.strike <= limit)));
  if (!ok.length) return null;
  let best = ok[0];
  for (let i = 1; i < ok.length; i++) if (Math.abs(ok[i].price - target) < Math.abs(best.price - target)) best = ok[i];
  const clamped = limit != null && best.strike === limit && best.price < target * 0.9;
  return { strike: best.strike, price: best.price, clamped };
}

/**
 * The monthly-strangle entry: start from the FARTHEST listed put and call that have a
 * bid, then move the cheaper side toward the money until its premium is closest to the
 * dearer side's. Only the cheaper side moves.
 */
export function tplFarMatched(rows: TplRow[], spot: number): { ce: number; pe: number } | null {
  const pes = rows.filter(r => r.strike < spot && ((r.pe && r.pe.bid) || 0) > 0).map(r => r.strike);
  const ces = rows.filter(r => r.strike > spot && ((r.ce && r.ce.bid) || 0) > 0).map(r => r.strike);
  if (!pes.length || !ces.length) return null;
  let pe = Math.min.apply(null, pes), ce = Math.max.apply(null, ces);
  const px = (t: TplOptType, k: number) => tplMarkOf(tplQuoteOf(rows.find(r => r.strike === k), t)) || 0;
  const pp = px('PE', pe), cp = px('CE', ce);
  if (!(pp > 0) || !(cp > 0)) return null;
  if (cp < pp) {
    const m = tplMatchStrike('CE', tplSideQuotes(rows, 'CE').filter(q => q.strike > spot), pp, null);
    if (m) ce = m.strike;
  } else if (pp < cp) {
    const m = tplMatchStrike('PE', tplSideQuotes(rows, 'PE').filter(q => q.strike < spot), cp, null);
    if (m) pe = m.strike;
  }
  return { ce, pe };
}

// ── the ready-mades ───────────────────────────────────────────────────────────

function ratio(c: TplCtx, t: TplOptType): TplLegSpec[] | null {
  const p0 = tplMarkOf(tplQuoteOf(c.rows.find(r => r.strike === c.atm), t));
  if (!p0) return null;
  const otm = c.rows.filter(r => (t === 'CE' ? r.strike > c.atm : r.strike < c.atm))
    .sort((a, b) => (t === 'CE' ? a.strike - b.strike : b.strike - a.strike));
  const pick = otm.find(r => { const p = tplMarkOf(tplQuoteOf(r, t)); return p != null && p <= p0 / 2; });
  return pick ? [{ action: 'SELL', t, strike: c.atm }, { action: 'BUY', t, strike: pick.strike, lots: 2 }] : null;
}

export const OPTION_TEMPLATES: OptionTemplate[] = [
  { id: 'long_call', name: 'Long Call', outlook: 'Bullish', blurb: 'Up move, risk = premium', glyph: '2,14 20,14 38,2',
    build: c => [{ action: 'BUY', t: 'CE', strike: c.atm }] },
  { id: 'bull_call_spread', name: 'Bull Call', outlook: 'Bullish', blurb: 'Moderately up, capped risk', glyph: '2,15 14,15 26,5 38,5',
    build: c => { const k2 = byDelta(c, 'CE', 0.3); return k2 && k2 !== c.atm ? [{ action: 'BUY', t: 'CE', strike: c.atm }, { action: 'SELL', t: 'CE', strike: k2 }] : null; } },
  { id: 'bull_put_spread', name: 'Bull Put (credit)', outlook: 'Bullish', blurb: 'Stays above, collect premium', glyph: '2,16 12,16 24,6 38,6',
    build: c => { const k = byDelta(c, 'PE', 0.3); const w = k != null ? wing(c, 'PE', k, 2) : null; return k && w && w !== k ? [{ action: 'SELL', t: 'PE', strike: k }, { action: 'BUY', t: 'PE', strike: w }] : null; } },
  { id: 'call_ratio', name: 'Call Ratio 1×2', outlook: 'Bullish', blurb: 'Big up move, ~zero cost', glyph: '2,10 16,10 22,15 38,2',
    build: c => ratio(c, 'CE') },
  { id: 'long_put', name: 'Long Put', outlook: 'Bearish', blurb: 'Down move, risk = premium', glyph: '2,2 20,14 38,14',
    build: c => [{ action: 'BUY', t: 'PE', strike: c.atm }] },
  { id: 'bear_put_spread', name: 'Bear Put', outlook: 'Bearish', blurb: 'Moderately down, capped risk', glyph: '2,5 14,5 26,15 38,15',
    build: c => { const k2 = byDelta(c, 'PE', 0.3); return k2 && k2 !== c.atm ? [{ action: 'BUY', t: 'PE', strike: c.atm }, { action: 'SELL', t: 'PE', strike: k2 }] : null; } },
  { id: 'bear_call_spread', name: 'Bear Call (credit)', outlook: 'Bearish', blurb: 'Stays below, collect premium', glyph: '2,6 14,6 26,16 38,16',
    build: c => { const k = byDelta(c, 'CE', 0.3); const w = k != null ? wing(c, 'CE', k, 2) : null; return k && w && w !== k ? [{ action: 'SELL', t: 'CE', strike: k }, { action: 'BUY', t: 'CE', strike: w }] : null; } },
  { id: 'put_ratio', name: 'Put Ratio 1×2', outlook: 'Bearish', blurb: 'Big down move, ~zero cost', glyph: '2,2 18,15 24,10 38,10',
    build: c => ratio(c, 'PE') },
  { id: 'monthly_strangle', name: 'Monthly Strangle', outlook: 'Neutral', blurb: 'Far strikes, premiums matched', glyph: '2,18 8,5 32,5 38,18',
    build: c => { const fm = tplFarMatched(c.rows, c.spot); return fm ? [{ action: 'SELL', t: 'PE', strike: fm.pe }, { action: 'SELL', t: 'CE', strike: fm.ce }] : null; } },
  { id: 'short_strangle', name: 'Short Strangle', outlook: 'Neutral', blurb: 'Range-bound, collect premium', glyph: '2,18 12,5 28,5 38,18',
    build: c => { const kc = byDelta(c, 'CE', 0.2), kp = byDelta(c, 'PE', 0.2); return kc && kp ? [{ action: 'SELL', t: 'CE', strike: kc }, { action: 'SELL', t: 'PE', strike: kp }] : null; } },
  { id: 'iron_condor', name: 'Iron Condor', outlook: 'Neutral', blurb: 'Range-bound, capped risk', glyph: '2,16 10,16 15,5 25,5 30,16 38,16',
    build: c => {
      const kc = byDelta(c, 'CE', 0.2), kp = byDelta(c, 'PE', 0.2);
      const wc = kc != null ? wing(c, 'CE', kc, 2) : null, wp = kp != null ? wing(c, 'PE', kp, 2) : null;
      return kc && kp && wc && wp && wc !== kc && wp !== kp
        ? [{ action: 'BUY', t: 'PE', strike: wp }, { action: 'SELL', t: 'PE', strike: kp }, { action: 'SELL', t: 'CE', strike: kc }, { action: 'BUY', t: 'CE', strike: wc }] : null;
    } },
  { id: 'iron_butterfly', name: 'Iron Butterfly', outlook: 'Neutral', blurb: 'Pins near spot, capped risk', glyph: '2,16 10,16 20,3 30,16 38,16',
    build: c => {
      const wc = byDelta(c, 'CE', 0.15), wp = byDelta(c, 'PE', 0.15);
      return wc && wp && wc !== c.atm && wp !== c.atm
        ? [{ action: 'BUY', t: 'PE', strike: wp }, { action: 'SELL', t: 'PE', strike: c.atm }, { action: 'SELL', t: 'CE', strike: c.atm }, { action: 'BUY', t: 'CE', strike: wc }] : null;
    } },
  { id: 'short_straddle', name: 'Short Straddle', outlook: 'Neutral', blurb: 'Pins at strike, max premium', glyph: '2,18 20,3 38,18',
    build: c => [{ action: 'SELL', t: 'CE', strike: c.atm }, { action: 'SELL', t: 'PE', strike: c.atm }] },
  { id: 'long_straddle', name: 'Long Straddle', outlook: 'Volatile', blurb: 'Big move either way', glyph: '2,2 20,17 38,2',
    build: c => [{ action: 'BUY', t: 'CE', strike: c.atm }, { action: 'BUY', t: 'PE', strike: c.atm }] },
  { id: 'long_strangle', name: 'Long Strangle', outlook: 'Volatile', blurb: 'Bigger move, cheaper', glyph: '2,2 14,15 26,15 38,2',
    build: c => { const kc = byDelta(c, 'CE', 0.3), kp = byDelta(c, 'PE', 0.3); return kc && kp ? [{ action: 'BUY', t: 'CE', strike: kc }, { action: 'BUY', t: 'PE', strike: kp }] : null; } },
];

export const findTemplate = (id: string): OptionTemplate | null => {
  for (const t of OPTION_TEMPLATES) if (t.id === id) return t;
  return null;
};

/** Which way a price level points: NB/BB open longs (Bullish), NS/BS shorts (Bearish). */
export type TplLevelKey = 'nbuy' | 'nshort' | 'bbuy' | 'bshort';
export const levelOutlook = (k: TplLevelKey): TplOutlook => (k === 'nbuy' || k === 'bbuy' ? 'Bullish' : 'Bearish');

/** Templates for a level, its own direction first; Neutral/Volatile follow ("not directional"). */
export function templatesForLevel(k: TplLevelKey): Array<{ tpl: OptionTemplate; directional: boolean }> {
  const want = levelOutlook(k);
  const own = OPTION_TEMPLATES.filter(t => t.outlook === want).map(tpl => ({ tpl, directional: true }));
  const other = OPTION_TEMPLATES.filter(t => t.outlook === 'Neutral' || t.outlook === 'Volatile').map(tpl => ({ tpl, directional: false }));
  return own.concat(other);
}

// ── expiry rule ───────────────────────────────────────────────────────────────

/**
 * Which expiry a fired level trades. Resolved AT FIRE TIME from the expiries listed
 * then, so an armed level never points at an expiry that has died.
 *   D0 — the nearest expiry          D1 — the one after it
 *   W0 — the nearest Friday expiry   M0 — the nearest last-Friday-of-month expiry
 * (Delta: dailies settle every day; weeklies and monthlies on Fridays.)
 * An expiry closer than `minHours` (and never closer than MIN_FIRE_MINUTES) is skipped
 * for the next one of the same kind — `rolled` says that happened.
 */
export type TplExpiryTier = 'D0' | 'D1' | 'W0' | 'M0';
export interface TplExpiryRule { tier: TplExpiryTier; minHours?: number | null }
export const DEFAULT_MIN_HOURS: Record<TplExpiryTier, number> = { D0: 6, D1: 6, W0: 48, M0: 168 };
export const MIN_FIRE_MINUTES = 30;

function isFriday(iso: string): boolean { return new Date(iso.slice(0, 10) + 'T00:00:00Z').getUTCDay() === 5; }
function isLastFriday(iso: string): boolean {
  if (!isFriday(iso)) return false;
  const d = new Date(iso.slice(0, 10) + 'T00:00:00Z');
  const n = new Date(d.getTime() + 7 * 86400e3);
  return n.getUTCMonth() !== d.getUTCMonth();
}

export function resolveExpiry(expiries: TplExpiry[], rule: TplExpiryRule, nowMs: number): { iso: string; settleMs: number; rolled: boolean } | null {
  const minH = rule.minHours != null && rule.minHours >= 0 ? rule.minHours : DEFAULT_MIN_HOURS[rule.tier];
  const floorMs = Math.max(minH * 3600e3, MIN_FIRE_MINUTES * 60e3);
  const live = expiries.filter(e => e.settleMs > nowMs).slice().sort((a, b) => a.settleMs - b.settleMs);
  const ofKind = rule.tier === 'W0' ? live.filter(e => isFriday(e.iso))
    : rule.tier === 'M0' ? live.filter(e => isLastFriday(e.iso))
    : live;
  // The natural pick (ignoring the time floor), then the first that clears the floor.
  const naturalIdx = rule.tier === 'D1' ? 1 : 0;
  const natural = ofKind[naturalIdx];
  if (!natural) return null;
  // Usable = clears the time floor. D1 is always one step past D0's pick.
  const ok = ofKind.filter(e => e.settleMs - nowMs >= floorMs);
  const chosen = ok[naturalIdx];
  if (!chosen) return null;
  return { iso: chosen.iso, settleMs: chosen.settleMs, rolled: chosen.iso !== natural.iso };
}

/**
 * A SPECIFIC expiry date chosen by the user (instead of a tier). It is used as is: no
 * roll to another date. null when it has settled or is closer than the time floor
 * (`minHours`, never under MIN_FIRE_MINUTES) at `nowMs`.
 */
export function resolveFixedExpiry(expiries: TplExpiry[], iso: string, minHours: number | null | undefined, nowMs: number): { iso: string; settleMs: number; rolled: boolean } | null {
  const e = expiries.find(x => x.iso === iso);
  if (!e) return null;
  const floorMs = Math.max((minHours != null && minHours >= 0 ? minHours : 0) * 3600e3, MIN_FIRE_MINUTES * 60e3);
  if (e.settleMs - nowMs < floorMs) return null;
  return { iso: e.iso, settleMs: e.settleMs, rolled: false };
}

/** The tier a specific date falls in — only used for tier-based defaults (pre-expiry exit). */
export function tierOfExpiry(expiries: TplExpiry[], iso: string, nowMs: number): TplExpiryTier {
  const live = expiries.filter(e => e.settleMs > nowMs).slice().sort((a, b) => a.settleMs - b.settleMs);
  if (live.length && live[0].iso === iso) return 'D0';
  if (isLastFriday(iso)) return 'M0';
  if (isFriday(iso)) return 'W0';
  return 'D1';
}

// ── build ─────────────────────────────────────────────────────────────────────

export interface TplBuiltLeg {
  action: TplSide; optionType: TplOptType; strike: number; lots: number;
  symbol: string; price: number; bid: number | null; ask: number | null; mark: number | null; iv: number | null;
}

/**
 * Legs the user edited (strikes, lots, legs added or removed). Stored on the attachment and
 * rebuilt at the fire:
 *   FIXED  — exactly `strike` (the chosen expiry must list it, priced)
 *   OFFSET — `offset` listed strikes from the ATM at the fire (0 = ATM, +1 = next strike up),
 *            so the shape follows price when the level fires far from today's spot.
 */
export type TplStrikeMode = 'FIXED' | 'OFFSET';
export interface TplCustomLeg { action: TplSide; t: TplOptType; strike: number; offset: number; lots: number }
export const MAX_CUSTOM_LEGS = 6;

/** Listed strikes priced on side `t`, ascending. */
function pricedStrikes(rows: TplRow[], t: TplOptType): number[] {
  return rows.filter(r => tplMarkOf(tplQuoteOf(r, t)) != null).map(r => r.strike).sort((a, b) => a - b);
}

/** Offsets of built legs from the ATM, in listed strikes of the whole row set (for OFFSET mode). */
export function legOffset(rows: TplRow[], atm: number, strike: number): number {
  const ks = rows.map(r => r.strike).sort((a, b) => a - b);
  const ia = ks.indexOf(atm), ik = ks.indexOf(strike);
  if (ia < 0 || ik < 0) return 0;
  return ik - ia;
}

/** The strike `offset` listed strikes from the ATM (clamped to the listed range). */
export function strikeAtOffset(rows: TplRow[], atm: number, offset: number): number | null {
  const ks = rows.map(r => r.strike).sort((a, b) => a - b);
  const ia = ks.indexOf(atm);
  if (ia < 0) return null;
  const v = ks[Math.max(0, Math.min(ks.length - 1, ia + Math.round(offset)))];
  return v == null ? null : v;
}

/** Price one leg spec on `rows`: SELL at the bid, BUY at the ask, else the mark. */
function priceSpec(rows: TplRow[], s: TplLegSpec): TplBuiltLeg | null {
  const q = tplQuoteOf(rows.find(r => r.strike === s.strike), s.t);
  if (!q) return null;
  const mark = tplMarkOf(q);
  const touch = s.action === 'SELL' ? q.bid : q.ask;
  const price = touch != null && touch > 0 ? touch : mark;
  if (price == null) return null;
  return { action: s.action, optionType: s.t, strike: s.strike, lots: s.lots || 1, symbol: q.symbol, price, bid: q.bid, ask: q.ask, mark, iv: q.iv };
}

/** Build the user's edited legs on one expiry's rows at `spot`. null = the chain cannot host them. */
export function buildCustomLegs(legs: TplCustomLeg[], mode: TplStrikeMode, rows: TplRow[], spot: number): TplBuiltLeg[] | null {
  if (!legs || !legs.length) return null;
  const atm = tplAtm(rows, spot);
  if (atm == null) return null;
  const out: TplBuiltLeg[] = [];
  for (const l of legs) {
    const k = mode === 'OFFSET' ? strikeAtOffset(rows, atm, l.offset) : l.strike;
    if (k == null || pricedStrikes(rows, l.t).indexOf(k) < 0) return null;
    const b = priceSpec(rows, { action: l.action, t: l.t, strike: k, lots: Math.max(1, Math.floor(l.lots || 1)) });
    if (!b) return null;
    out.push(b);
  }
  return out;
}

/** Validate stored custom legs (server-side arm and the dialog). Returns the reason or null. */
export function customLegsProblem(legs: any, mode: any): string | null {
  if (legs == null) return null;
  if (!Array.isArray(legs) || !legs.length) return 'custom legs must be a non-empty list';
  if (legs.length > MAX_CUSTOM_LEGS) return 'at most ' + MAX_CUSTOM_LEGS + ' legs';
  if (mode !== 'FIXED' && mode !== 'OFFSET') return 'strikeMode must be FIXED or OFFSET';
  for (const l of legs) {
    if (!l || (l.action !== 'BUY' && l.action !== 'SELL')) return 'each leg needs action BUY or SELL';
    if (l.t !== 'CE' && l.t !== 'PE') return 'each leg needs t CE or PE';
    const lots = Number(l.lots);
    if (!(Math.floor(lots) === lots && lots >= 1 && lots <= 20)) return 'leg lots must be a whole number 1–20';
    if (mode === 'FIXED' && !(Number(l.strike) > 0)) return 'FIXED legs need a strike';
    const off = Number(l.offset);
    if (mode === 'OFFSET' && !(Math.floor(off) === off && off >= -80 && off <= 80)) return 'OFFSET legs need a whole-number offset −80…80';
  }
  return null;
}

/** Net premium per 1 lot of the built legs: + = credit received, − = debit paid (Delta units). */
export function builtNetPremium(legs: TplBuiltLeg[]): number {
  return legs.reduce((s, l) => s + (l.action === 'SELL' ? 1 : -1) * l.price * l.lots, 0);
}

/** Build a template on one expiry's rows at `spot`. null = the chain cannot host it. */
export function buildTemplate(tpl: OptionTemplate, rows: TplRow[], spot: number, settleMs: number, nowMs: number): TplBuiltLeg[] | null {
  const atm = tplAtm(rows, spot);
  if (atm == null) return null;
  const T = Math.max(0, settleMs - nowMs) / YEAR_MS;
  const spec = tpl.build({ rows, spot, atm, T });
  if (!spec || !spec.length) return null;
  const out: TplBuiltLeg[] = [];
  for (const s of spec) {
    const q = tplQuoteOf(rows.find(r => r.strike === s.strike), s.t);
    if (!q) return null;
    const mark = tplMarkOf(q);
    const touch = s.action === 'SELL' ? q.bid : q.ask;
    const price = touch != null && touch > 0 ? touch : mark;
    if (price == null) return null;
    out.push({ action: s.action, optionType: s.t, strike: s.strike, lots: s.lots || 1, symbol: q.symbol, price, bid: q.bid, ask: q.ask, mark, iv: q.iv });
  }
  return out;
}

/**
 * "If it fired at `toSpot`": the chain re-priced with Black-Scholes at another spot,
 * each contract keeping its own IV (no smile shift). Bid/ask move with the mark in
 * proportion, so the spread stays the same share of the price. Used for the preview
 * at the level; the real fire uses the live chain at that moment.
 */
export function chainAtSpot(rows: TplRow[], toSpot: number, settleMs: number, nowMs: number): TplRow[] {
  const T = Math.max(0, settleMs - nowMs) / YEAR_MS;
  const shift = (q: TplQuote | null, t: TplOptType, K: number): TplQuote | null => {
    if (!q) return null;
    const m0 = tplMarkOf(q);
    if (!q.iv || !(q.iv > 0) || m0 == null) return q;
    const m1 = tplBsPrice(t, toSpot, K, T, q.iv);
    const r = m0 > 0 ? m1 / m0 : 1;
    return {
      symbol: q.symbol, iv: q.iv, mark: m1,
      bid: q.bid != null ? q.bid * r : null, ask: q.ask != null ? q.ask * r : null,
      delta: tplBsDelta(t, toSpot, K, T, q.iv),
    };
  };
  return rows.map(r => ({ strike: r.strike, ce: shift(r.ce, 'CE', r.strike), pe: shift(r.pe, 'PE', r.strike) }));
}

/**
 * Can this leg be traded right now? A SELL needs a bid to sell into, a BUY an ask
 * to lift, and when both sides are quoted the spread must be within `maxSpreadPct`
 * of the mid. Returns the reason it cannot, or null.
 */
export function legLiquidityProblem(leg: { action: TplSide; symbol: string; bid: number | null; ask: number | null }, maxSpreadPct: number): string | null {
  const bid = leg.bid != null && leg.bid > 0 ? leg.bid : null;
  const ask = leg.ask != null && leg.ask > 0 ? leg.ask : null;
  if (leg.action === 'SELL' && bid == null) return leg.symbol + ': no bid to sell into';
  if (leg.action === 'BUY' && ask == null) return leg.symbol + ': no ask to buy from';
  if (bid != null && ask != null) {
    const mid = (bid + ask) / 2;
    const pct = mid > 0 ? ((ask - bid) / mid) * 100 : Infinity;
    if (pct > maxSpreadPct) return leg.symbol + ': spread ' + pct.toFixed(0) + '% of mid (limit ' + maxSpreadPct + '%)';
  }
  return null;
}
